import { useMutation, useQuery } from '@tanstack/react-query';

import { PartnerData } from '@app/components/Admin/Integrations/partials/integration-settings-types';
import {
  CREATE_INTEGRATION_CONFIG,
  DELETE_INTEGRATION_CONFIG,
  GET_INTEGRATION_CONFIG,
  GET_INTEGRATION_PARTNERS,
  UPDATE_INTEGRATION_CONFIG,
} from '@app/constants/endpoints';
import {
  deleteData,
  fetchData,
  postData,
  putData,
} from '@app/hooks/react-query-helpers';
import { makeToastT } from '@app/i18n';
import queryClient from '@app/query-client';
import { PartnerWorkflowConfig } from '@app/types/api/organization';
import { toaster } from '@chakra-primitives/toaster';

const t = makeToastT();

const TOASTER_DURATION = 5000;

// Shape of the cached integration-config query that partner-workflow toggles
// optimistically patch (enabled / initiationEnabled) so the switch flips
// instantly instead of waiting for the PUT + refetch.
type IntegrationConfigCache = {
  partnerWorkflows?: PartnerWorkflowConfig[];
  [key: string]: unknown;
};

export const createErrorToaster = (title: string, message: string) => {
  toaster.create({
    title,
    description: message,
    type: 'error',
    duration: TOASTER_DURATION,
  });
};

export const integrationPartnersLoader = async (): Promise<
  PartnerData[] | null
> => {
  try {
    const data = await queryClient.fetchQuery({
      queryKey: ['integrationPartners'],
      queryFn: () =>
        fetchData(GET_INTEGRATION_PARTNERS(), { handleError: true }),
      staleTime: Infinity,
    });
    return data;
  } catch (error) {
    createErrorToaster(
      t('integrationConfig.loadPartnersFailed.title'),
      (error as Error)?.message || String(error),
    );
    return null;
  }
};

export const partnerConfigLoader = async (args?: {
  params: Record<string, string | undefined>;
}): Promise<PartnerData[] | null> => {
  const { params } = args ?? { params: {} };
  const { partnerName } = params;
  const partners = await integrationPartnersLoader();
  if (partnerName) {
    queryClient.prefetchQuery({
      queryKey: ['integrationConfig', partnerName],
      queryFn: () =>
        fetchData(GET_INTEGRATION_CONFIG(partnerName), { handleError: true }),
      staleTime: 5 * (60 * 1000),
    });
  }
  return partners;
};

export const useGetIntegrationConfigQuery = (integrationName?: string) => {
  const { data, isRefetching, isLoading, isError, error } = useQuery({
    queryKey: ['integrationConfig', integrationName],
    queryFn: () =>
      fetchData(GET_INTEGRATION_CONFIG(integrationName), {
        handleError: true,
      }),
    enabled: !!integrationName,
  });

  let errorMessage: string | null = null;
  if (isError) {
    errorMessage = (error as Error).message;
    // Only show error toast if it's not a "not found" error (404)
    // Missing integration config is an expected state for new/unconfigured integrations
    const isNotFoundError = errorMessage?.toLowerCase().includes('not found');
    if (!isNotFoundError) {
      createErrorToaster(
        t('integrationConfig.fetchConfigFailed.title'),
        errorMessage,
      );
    }
  }

  return {
    data,
    isRefetching,
    isLoading,
    errorMessage,
  };
};

export const useGetIntegrationPartnersQuery = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['integrationPartners'],
    queryFn: () => fetchData(GET_INTEGRATION_PARTNERS(), { handleError: true }),
  });
  return { partners: data, isError, isLoading };
};

export const useUpdateIntegrationConfigMutation = (
  partnerName: string,
  successCallback?: () => void,
  errorCallback?: (error: Error) => void,
) => {
  const queryKey = ['integrationConfig', partnerName];
  return useMutation({
    // Optimistically flip the workflow toggle in cache; rolled back in onError.
    onMutate: async (updates: {
      partnerName?: string;
      [key: string]: unknown;
    }) => {
      if (updates.configSetting !== 'partnerWorkflows' || !updates.configData) {
        return undefined;
      }
      const configData =
        updates.configData as Partial<PartnerWorkflowConfig> & {
          id: string;
        };
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<IntegrationConfigCache>(queryKey);
      if (previous?.partnerWorkflows) {
        queryClient.setQueryData<IntegrationConfigCache>(queryKey, {
          ...previous,
          partnerWorkflows: previous.partnerWorkflows.map((wf) =>
            wf.id === configData.id ? { ...wf, ...configData } : wf,
          ),
        });
      }
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['integrationPartners'] });
      if (successCallback) {
        successCallback();
      }
    },
    onError: (error: Error, _updates, context) => {
      const previous = (
        context as { previous?: IntegrationConfigCache } | undefined
      )?.previous;
      if (previous !== undefined) {
        queryClient.setQueryData(queryKey, previous);
      }
      if (errorCallback) {
        errorCallback(error);
      }
    },
    mutationFn: async (updates: {
      partnerName?: string;
      [key: string]: unknown;
    }) =>
      putData(
        UPDATE_INTEGRATION_CONFIG(updates.overrideRuleset),
        {
          partnerName,
          ...updates,
        },
        { handleError: true },
      ),
  });
};

export const useIntegrationEnablementCheck = (integrationName?: string) => {
  const { data } = useGetIntegrationConfigQuery(integrationName);
  return !!data?.enabled;
};

export const useCreateIntegrationConfigMutation = (
  partnerName: string,
  successCallback?: () => void,
  cleanUpComponent?: () => void,
  invalidateQueries: boolean = true,
  errorLabel: string = t('integrationConfig.sellerCodeLabel'),
) => {
  const queryKey = ['integrationConfig', partnerName];

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (body: any) =>
      postData(
        CREATE_INTEGRATION_CONFIG(),
        { partnerName, ...body },
        { handleError: true },
      ),
    onSuccess: () => {
      if (invalidateQueries) {
        queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
        queryClient.invalidateQueries({ queryKey: ['integrationPartners'] });
      }
      if (successCallback) {
        successCallback();
      }
      if (cleanUpComponent) {
        cleanUpComponent();
      }
    },
    onError: (error) => {
      createErrorToaster(
        t('integrationConfig.createFailed.title', { label: errorLabel }),
        (error as Error).message,
      );
    },
  });
};

export const useDeleteIntegrationMappingMutation = (
  partnerName: string,
  integrationId: string,
  cleanUpComponent: () => void,
) => {
  const queryKey = ['integrationConfig', partnerName];

  const { mutate: deleteMutation, isLoading: isDeletePending } = useMutation({
    mutationFn: async () =>
      deleteData(DELETE_INTEGRATION_CONFIG(partnerName, integrationId), {
        handleError: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
      toaster.create({
        title: t('integrationConfig.sellerCodeDeleted.title'),
        description: t('integrationConfig.sellerCodeDeleted.description', {
          integrationId,
        }),
        type: 'success',
        duration: TOASTER_DURATION,
      });
      cleanUpComponent();
    },
    onError: (error) => {
      createErrorToaster(
        t('integrationConfig.deleteSellerCodeFailed.title'),
        (error as Error).message,
      );
    },
  });

  return { deleteMutation, isDeletePending };
};
